import React, { useMemo, useState } from 'react'
import {
  ColumnDef,
  RowData,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { DataTablePagination } from './data-table-pagination'
import { useTranslation } from 'react-i18next'
import { useColumnVisibility } from '@/hooks/use-column-visibility'

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    className: string
  }
}

import { DataTableToolbar } from './data-table/data-table-toolbar'
import { FilterConfig } from './data-table/types'

// ... imports

interface DataTableProps<TData> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<TData, any>[]
  data: TData[]
  pages?: number
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  params?: any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setParams?: React.Dispatch<React.SetStateAction<any>>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ToolbarName?: any
  filterConfig?: FilterConfig
  onRowClick?: (row: TData) => void
  hideHeader?: boolean
  showPagination?: boolean
  withColumnBorders?: boolean
  initialColumnVisibility?: VisibilityState
  /**
   * Unique storage key for persisting column visibility in localStorage
   * If not provided, column visibility will not be persisted
   * Example: 'cases-table', 'users-table', 'clients-table'
   */
  storageKey?: string
}

export function DataTable<TData>({
  columns,
  data,
  params,
  setParams,
  pages,
  ToolbarName,
  filterConfig,
  onRowClick,
  hideHeader,
  showPagination = true,
  initialColumnVisibility = {},
  storageKey
}: DataTableProps<TData>) {
  const i18n = useTranslation()
  const [rowSelection, setRowSelection] = useState({})

  // Use persistent column visibility if storageKey is provided, otherwise use regular state
  const [columnVisibility, setColumnVisibility] = storageKey
    ? useColumnVisibility(storageKey, initialColumnVisibility)
    : useState<VisibilityState>(initialColumnVisibility)

  const page_index = useMemo(() => {
    return params ? Math.floor(params.page / params.page_size) : 0
  }, [params])

  const table_page_size = useMemo(() => {
    return params ? params.page_size : data.length
  }, [params, data])

  const sortingState = useMemo(() => {
    if (!params?.ordering) return []
    const isDesc = params.ordering.startsWith('-')
    const id = isDesc ? params.ordering.substring(1) : params.ordering
    return [{ id, desc: isDesc }]
  }, [params?.ordering])

  const table = useReactTable({
    data,
    columns,
    pageCount: pages ?? 1,
    state: {
      columnVisibility,
      rowSelection,
      sorting: sortingState,
      pagination: {
        pageIndex: page_index,
        pageSize: table_page_size,
      }
    },
    enableRowSelection: true,
    onSortingChange: (updater) => {
      if (!params || !setParams) return;
      const newSorting =
        typeof updater === 'function' ? updater(sortingState) : updater

      if (newSorting.length > 0) {
        const { id, desc } = newSorting[0]
        const ordering = desc ? '-' + id : id
        setParams({ ...params, ordering })
      } else {
        setParams({ ...params, ordering: null })
      }
    },
    onPaginationChange: (updater) => {
      if (!params || !setParams) return; // Guard clause
      const newPagination =
        typeof updater === 'function'
          ? updater({
            pageIndex: page_index,
            pageSize: params.page_size,
          })
          : updater

      const page_offset = newPagination.pageIndex * newPagination.pageSize

      setParams({ ...params, page: page_offset, page_size: newPagination.pageSize })
    },
    onRowSelectionChange: setRowSelection,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    manualPagination: !!params, // Set manualPagination based on params presence
    manualSorting: !!params // Enable manualSorting when handling sorting externally
  })

  return (
    <div className='space-y-4'>
      {ToolbarName && params && setParams && <ToolbarName table={table} params={params} setParams={setParams} />}
      {!ToolbarName && (
        <DataTableToolbar
          table={table}
          filterConfig={filterConfig}
          params={params}
          setParams={setParams}
        />
      )}
      {/* Refined enterprise table surface — elevated card, refined header & rows */}
      <div className="rounded-xl border border-border/60 bg-card shadow-legal-sm overflow-hidden">
        <Table className="w-full min-w-max">
          {!hideHeader && (
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow
                  key={headerGroup.id}
                  className='group/row border-b-2 border-border/70 bg-muted/40 hover:bg-muted/40'
                >
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      colSpan={header.colSpan}
                      className={`h-11 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground ${header.column.columnDef.meta?.className ?? ''} ${header.index !== (headerGroup.headers.length - 1) ? 'border-l border-border/40' : ''}`}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
          )}
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  className={`group/row border-b border-border/40 transition-colors last:border-b-0 data-[state=selected]:bg-primary/[0.08] ${onRowClick ? 'cursor-pointer hover:bg-primary/[0.04]' : 'hover:bg-muted/40'} ${hideHeader ? 'border-t-0' : ''}`}
                  onClick={(e) => {
                    if (onRowClick && !(e.target as HTMLElement).closest('[data-cell-id="actions"]')) {
                      onRowClick(row.original);
                    }
                  }}
                >
                  {row.getVisibleCells().map((cell, cellIndex) => (
                    <TableCell
                      key={cell.id}
                      data-cell-id={cell.column.id}
                      className={`px-3 py-3.5 align-middle text-sm ${cell.column.columnDef.meta?.className ?? ''} ${hideHeader ? 'p-2' : ''} ${cellIndex !== (row.getVisibleCells().length - 1) ? 'border-l border-border/30' : ''}`}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow className='hover:bg-transparent'>
                <TableCell
                  colSpan={columns.length}
                  className='h-32 text-center'
                >
                  <div className='flex flex-col items-center justify-center gap-1.5 text-muted-foreground'>
                    <span className='text-sm font-medium'>{i18n.t('no_results')}</span>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {showPagination && <DataTablePagination table={table} />}
    </div>
  )
}
